<script setup lang="ts">
    import { computed, inject, watch } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { createEvent } from '@cwd/Views/Event/Event.access';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import EventList from './EventList.vue';
    import EventCopySettingsModal from './EventCopySettingsModal.vue';
    import { type Event as EventItem, type EventInList } from './EventTypes';
    import Loading from '@shared/components/Loading.vue';
    import NewPreview from '@shared/components/Editor/NewPreview.vue';
    import { combineUrlSegments } from '@shared/utils/combineUrlSegments';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { UserEventService } from '@shared/Root/UserEvent.service';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { setupCopyEvent } from './New';
    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const userEventService = injectorAccess.injector.get(UserEventService);
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const { copyEventState, onCopyEvent, handleCloseCopySettings } = setupCopyEvent();
    const state = copyEventState;

    // Watch for changes in requireAttestation and update the form
    watch(
        () => state.requireAttestation,
        (newValue) => {
            if (state.form.data.CopyAttestationMailing) {
                state.form.data.CopyAttestationMailing.modelValue = newValue;
                state.form.data.CopyAttestationMailing.checkbox!.disabled = !newValue;
            }
        }
    );

    const iframeSrc = computed(() => {
        if (!state.showPreview) return '';
        return combineUrlSegments(contextAccess.state.currentAccount!.PublicEventUrl.PublicHost, 'Events', '' + state.eventId, '?preview=true');
    });

    const handleShowPreview = (event: EventInList) => {
        if (state.isLoading) return;

        state.eventId = event.EventId;
        state.requireAttestation = event.RequireAttestation;
        state.sourceEventUseRelativeSchedule = event.UseRelativeSchedule ?? false;
        state.showPreview = true;
    };

    const handleDeselect = () => {
        state.eventId = null;
        state.showPreview = false;
        state.showCopySettings = false;
    };

    const handleShowCopySettings = () => {
        if (state.isLoading) return;
        state.showPreview = false;
        state.showCopySettings = true;
    };

    const copyRestrictedByRelativeSchedule = computed(() => state.sourceEventUseRelativeSchedule && !featureAccess.hasFeature('Events.RelativeSchedule'));

    const onCreateEvent = async () => {
        if (state.isLoading) return;
        state.isLoading = true;

        await createEvent()
            .then((data: EventItem) => {
                userEventService.registerUserEvent('Events', 'Created draft event', data.Name);
                window.location.replace('/Events/' + data.EventId + '?new=true');
            })
            .catch(() => {
                bsAlert({ content: 'Event could not be created.', type: AlertTypes.Danger });
                state.isLoading = false;
            });
    };
</script>

<template>
    <div class="p-lg-r p-lg-l">
        <header role="banner">
            <div class="d-flex justify-content-between align-items-baseline">
                <h1>{{ $Resources.NewEventHeading }}</h1>
            </div>

            <p class="m-lg-b">
                {{ $Resources.NewEventIntroduction }}
            </p>
        </header>

        <div class="form-group">
            <button type="button" class="btn btn-cta p-lg-r p-lg-l" @click="onCreateEvent" :disabled="state.isLoading">
                {{ $Resources.EventNewEvent }}
                <i class="fa fa-chevron-right" aria-hidden="true"></i>
            </button>
        </div>

        <h2>{{ $Resources.EventsCopyTab }}</h2>
    </div>

    <EventList :selectedId="state.eventId" @clickedEventId="(event: EventInList) => handleShowPreview(event)"></EventList>

    <NewPreview :iframeSrc="iframeSrc" @close="handleDeselect">
        <template #toolbar>
            <button type="button" class="btn btn-cta p-lg-r p-lg-l" @click="handleShowCopySettings" :disabled="state.isLoading">
                {{ $Resources.StartEditingBtn }}
                <i class="fa fa-chevron-right" aria-hidden="true"></i>
            </button>
        </template>
    </NewPreview>

    <EventCopySettingsModal
        :show="state.showCopySettings"
        :isLoading="state.isLoading"
        :formData="state.form.data"
        :copyRestrictedByRelativeSchedule="copyRestrictedByRelativeSchedule"
        :copySignUpFormMissing="state.copySignUpFormMissing"
        @close="handleCloseCopySettings"
        @update:modelValue="(key, newVal) => (state.form.data[key].modelValue = newVal)"
        @submit="onCopyEvent" />

    <Loading :show="state.isLoading" loadingStyle="progress-bar" :fixed="true"></Loading>
</template>
